import { useFilterMultipleState, useFilterState } from '@vtex/admin-ui'
import { useCache } from 'hooks'
import { useContext, useEffect } from 'react'
import type { SimplifiedPromotion } from 'typings'
import { PromotionsContext } from '../../../providers/PromotionsContextProvider'

interface FilterConfig {
  id: string
  filterFn: (item: SimplifiedPromotion) => boolean
}

export function usePromotionsFilterState(config: FilterConfig) {
  const state = useFilterState()
  const { get, set } = useCache()
  const context = useContext(PromotionsContext)

  if (context === undefined) {
    throw new Error(
      'usePromotionsFilterState must be used within a PromotionsContext'
    )
  }

  useEffect(() => {
    state.setAppliedItem(get(config.id))
  }, [])

  const { setFiltersState } = context

  const { appliedItem, onClear } = state

  useEffect(() => {
    setFiltersState((prev) => {
      const prevFilters = prev.filter((filter) => filter.id !== config.id)

      const values = appliedItem?.id ? [appliedItem?.id] : []

      const newFilters = [
        ...prevFilters,
        {
          id: config.id,
          filterFn: config.filterFn,
          values,
          clear: onClear,
        },
      ]

      setTimeout(() => set(config.id, appliedItem))

      return newFilters
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedItem])

  return state
}

export function usePromotionsFilterMultipleState(config: FilterConfig) {
  const state = useFilterMultipleState()
  const { get, set } = useCache()
  const context = useContext(PromotionsContext)

  if (context === undefined) {
    throw new Error(
      'useFilterMultipleState must be used within a PromotionsContext'
    )
  }

  const { setFiltersState } = context

  const { appliedItems, onClear, setAppliedItems } = state

  useEffect(() => {
    setAppliedItems(get(config.id, []))
  }, [])

  useEffect(() => {
    setFiltersState((prev) => {
      const prevFilters = prev.filter((f) => f.id !== config.id)

      const newFilters = [
        ...prevFilters,
        {
          id: config.id,
          filterFn: config.filterFn,
          values: appliedItems?.map((x) => x.id) ?? [],
          clear: onClear,
        },
      ]

      setTimeout(() => set(config.id, appliedItems))

      return newFilters
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedItems])

  return state
}
